import { Grid, makeStyles } from "@material-ui/core";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Route, RouteComponentProps, Switch } from "react-router";
import { PageError, SideMenu } from "src/common/components";
import Instructions from "src/common/contexts/Instructions";
import { withInstructions, LocalesContext } from "src/common/contexts";
import Routes from "src/common/Routes";
import CurrentUserProvider from "src/modules/CurrentUser/provider";
import CyclesView from "src/modules/Dashboard/Cycles/view";
import TemplateView from "src/modules/Dashboard/Templates/view";
import RedirectByContext from "./redirect";
import { DesktopRoute } from "src/common/Routes/DesktopRoute";
import { IWithInstructions } from "src/common/contexts/Instructions/intructionsProvider";

const useStyles = makeStyles((theme: any) => ({
  container: {
    "@media print": {
      maxWidth: "100% !important",
      width: "100% !important"
    },
    padding: "0 16px",
    width: "100%",
    [theme.breakpoints.up("md")]: {
      width: "calc(100% - 215px)"
    },
    [theme.breakpoints.up("lg")]: {
      maxWidth: "1280px",
      padding: "0",
      width: "calc(100% - 502px)"
    }
  },
  leftSideBar: {
    "@media print": {
      display: "none" as "none"
    },
    width: "215px",
    [theme.breakpoints.down("sm")]: {
      display: "none"
    },
    [theme.breakpoints.up("lg")]: {
      marginRight: "36px"
    }
  },
  rightSideBar: {
    "@media print": {
      display: "none" as "none"
    },
    marginLeft: "36px",
    width: "215px"
  }
}));
interface IRouteParams {
  lng: string;
  view: string;
  subView?: string;
}
type Props = RouteComponentProps<IRouteParams> & IWithInstructions;
const PageNotFound = () => {
  const { t } = useTranslation();
  return <PageError message={t("feedback.pageNotFound")} />;
};
const Dashboard = ({
  match: {
    params: { lng, view, subView }
  },
  instructionsKey,
  isTranslationKey,
  instructionsMap
}: Props) => {
  const classes = useStyles();
  const { locales } = React.useContext(LocalesContext);
  const availableLocales = locales.join("|");
  return (
    <CurrentUserProvider lng={lng}>
      <Grid justifyContent="space-between" container>
        <Grid className={classes.leftSideBar} item>
          <SideMenu currentPage={view} subPage={subView} lng={lng} />
        </Grid>
        <Grid className={classes.container} item>
          <Switch>
            <Route exact path={`/:lng(${availableLocales})/dashboard/:view(forward)`} component={RedirectByContext} />
            <Route
              exact
              path={`/:lng(${availableLocales})/dashboard/:view(my-employees)`}
              component={Routes.MyEmployees}
            />
            <Route
              exact
              path={`/:lng(${availableLocales})/dashboard/:view(my-direct-reports-action-plans)`}
              component={Routes.MyDirectReportsActionPlans}
            />
            <Route
              exact
              path={`/:lng(${availableLocales})/dashboard/:view(my-employees)/talk-sheet/:talkId`}
              component={Routes.TalkSheet}
            />
            <Route
              exact
              path={`/:lng(${availableLocales})/dashboard/:view(my-employees)/my-questionnaire/:questionnaireId`}
              component={Routes.MyQuestionnaire}
            />
            <Route
              exact
              path={`/:lng(${availableLocales})/dashboard/:view(manager-dashboard)/:id`}
              render={props => <DesktopRoute props={props} component={Routes.ManagerDashboard} />}
            />
            <Route
              exact
              path={`/:lng(${availableLocales})/dashboard/:view(my-employees)/peer-selection/:talkId`}
              component={Routes.PeerSelection}
            />
            <Route
              exact
              path={`/:lng(${availableLocales})/dashboard/:view(my-employees)/peer-summary/:talkId`}
              component={Routes.PeerSummary}
            />
            <Route
              exact
              path={`/:lng(${availableLocales})/dashboard/:view(my-employees)/anonymous-summary/:talkId`}
              component={Routes.AnonTeamSummaryPage}
            />
            <Route
              exact
              path={`/:lng(${availableLocales})/dashboard/:view(anonymous-feedback)`}
              component={Routes.AnonymousFeedback}
            />
            <Route
              exact
              path={`/:lng(${availableLocales})/dashboard/:view(anonymous-feedback)/:questionnaireId`}
              component={Routes.AnonymousFeedbackSummary}
            />
            <Route
              exact
              path={`/:lng(${availableLocales})/dashboard/:view(my-feedbacks)`}
              component={Routes.MyFeedbacks}
            />
            <Route
              exact
              path={`/:lng(${availableLocales})/dashboard/:view(my-action-plans)`}
              component={Routes.MyActionPlans}
            />
            <Route
              exact
              path={`/:lng(${availableLocales})/dashboard/:view(my-feedbacks)/talk-sheet/:talkId`}
              component={Routes.TalkSheet}
            />
            <Route
              exact
              path={`/:lng(${availableLocales})/dashboard/:view(my-feedbacks)/my-questionnaire/:questionnaireId`}
              component={Routes.MyQuestionnaire}
            />
            <Route
              exact
              path={`/:lng(${availableLocales})/dashboard/:view(my-feedbacks)/peer-selection/:talkId`}
              component={Routes.PeerSelection}
            />
            <Route
              exact
              path={`/:lng(${availableLocales})/dashboard/:view(my-feedbacks)/peer-summary/:talkId`}
              component={Routes.PeerSummary}
            />
            <Route exact path={`/:lng(${availableLocales})/dashboard/:view(my-peers)`} component={Routes.MyPeers} />
            <Route
              exact
              path={`/:lng(${availableLocales})/dashboard/:view(my-peers)/my-peer-questionnaire/:questionnaireId`}
              component={Routes.MyPeerQuestionnaire}
            />
            <Route
              path={`/:lng(${availableLocales})/dashboard/cycles`}
              render={props => <DesktopRoute props={props} component={CyclesView} />}
            />
            <Route
              exact
              path={`/:lng(${availableLocales})/dashboard/:view(admin-translators)`}
              render={props => <DesktopRoute props={props} component={Routes.AdminRoles} />}
            />
            <Route
              path={`/:lng(${availableLocales})/dashboard/:view(templates)/:subView`}
              render={props => <DesktopRoute props={props} component={TemplateView} />}
            />
            <Route component={PageNotFound} />
          </Switch>
        </Grid>
        <Grid className={classes.rightSideBar} item>
          <Instructions
            isTranslationKey={isTranslationKey}
            translationKey={instructionsKey}
            instructionsMap={instructionsMap}
          />
        </Grid>
      </Grid>
    </CurrentUserProvider>
  );
};
export default withInstructions(Dashboard);
